import React from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Container
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Logo color palette
    const logoColors = {
        primaryBlue: '#3f51b5',
        brightYellow: '#c4d835',
        darkBlue: '#2c387e',
        lightYellow: '#d4e157',
    };

    const isActive = (path) => location.pathname === path;

    return (
        <AppBar 
            position="sticky" 
            elevation={0}
            sx={{
                background: `linear-gradient(135deg, ${logoColors.primaryBlue}f0 0%, ${logoColors.darkBlue}f0 100%)`,
                backdropFilter: 'blur(20px)',
                borderBottom: `2px solid ${logoColors.brightYellow}40`,
                color: logoColors.brightYellow,
                boxShadow: `0 4px 20px ${logoColors.primaryBlue}20`,
            }}
        >
            <Container maxWidth="lg">
                <Toolbar sx={{ px: 0 }}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            flexGrow: 1,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                opacity: 0.9,
                                transform: 'translateY(-1px)'
                            }
                        }}
                        onClick={() => navigate('/')}
                    >   
                    <Box
                        component="img"
                        src="/favicon-32x32.png"
                        alt="Company Logo"
                        sx={{
                            mr: 2,
                            width: 32,
                            height: 32,
                            filter: `drop-shadow(0 2px 8px ${logoColors.brightYellow}40)`,
                        }}
                        />
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                fontWeight: 700,
                                background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '-0.02em',
                                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                            }}
                        >
                            The Grid - Futsal Court
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={() => navigate('/')}
                            variant={isActive('/') ? 'contained' : 'text'}
                            sx={{
                                minWidth: 'auto',
                                px: 3,
                                py: 1,
                                color: isActive('/') ? logoColors.darkBlue : logoColors.brightYellow,
                                fontWeight: 600,
                                borderRadius: '8px',
                                textTransform: 'none',
                                border: `1px solid ${logoColors.brightYellow}20`,
                                '&:hover': {
                                    backgroundColor: isActive('/') 
                                        ? logoColors.lightYellow
                                        : `${logoColors.brightYellow}20`,
                                    border: `1px solid ${logoColors.brightYellow}60`,
                                    transform: 'translateY(-2px)',
                                },
                                ...(isActive('/') && {
                                    background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                    boxShadow: `0 4px 15px ${logoColors.brightYellow}40`,
                                    border: `1px solid ${logoColors.brightYellow}`,
                                }),
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            Home
                        </Button>
                        
                        <Button
                            onClick={() => navigate('/booking')}
                            variant={isActive('/booking') ? 'contained' : 'text'}
                            sx={{
                                minWidth: 'auto',
                                px: 3,
                                py: 1,
                                color: isActive('/booking') ? logoColors.darkBlue : logoColors.brightYellow,
                                fontWeight: 600,
                                borderRadius: '8px',
                                textTransform: 'none',
                                border: `1px solid ${logoColors.brightYellow}20`,
                                '&:hover': {
                                    backgroundColor: isActive('/booking') 
                                        ? logoColors.lightYellow
                                        : `${logoColors.brightYellow}20`,
                                    border: `1px solid ${logoColors.brightYellow}60`,
                                    transform: 'translateY(-2px)',
                                },
                                ...(isActive('/booking') && {
                                    background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                    boxShadow: `0 4px 15px ${logoColors.brightYellow}40`,
                                    border: `1px solid ${logoColors.brightYellow}`,
                                }),
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            Book Court
                        </Button>

                        <Button
                            onClick={() => navigate('/about')}
                            variant={isActive('/about') ? 'contained' : 'text'}
                            sx={{
                                minWidth: 'auto',
                                px: 3,
                                py: 1,
                                color: isActive('/about') ? logoColors.darkBlue : logoColors.brightYellow,
                                fontWeight: 600,
                                borderRadius: '8px',
                                textTransform: 'none',
                                border: `1px solid ${logoColors.brightYellow}20`,
                                '&:hover': {
                                    backgroundColor: isActive('/about') 
                                        ? logoColors.lightYellow
                                        : `${logoColors.brightYellow}20`,
                                    border: `1px solid ${logoColors.brightYellow}60`,
                                    transform: 'translateY(-2px)',
                                },
                                ...(isActive('/about') && {
                                    background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                    boxShadow: `0 4px 15px ${logoColors.brightYellow}40`,
                                    border: `1px solid ${logoColors.brightYellow}`,
                                }),
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            About Us
                        </Button>

                        <Button
                            onClick={() => navigate('/contact')}
                            variant={isActive('/contact') ? 'contained' : 'text'}
                            sx={{
                                minWidth: 'auto',
                                px: 3,
                                py: 1,
                                color: isActive('/contact') ? logoColors.darkBlue : logoColors.brightYellow,
                                fontWeight: 600,
                                borderRadius: '8px',
                                textTransform: 'none',
                                border: `1px solid ${logoColors.brightYellow}20`,
                                '&:hover': {
                                    backgroundColor: isActive('/contact') 
                                        ? logoColors.lightYellow
                                        : `${logoColors.brightYellow}20`,
                                    border: `1px solid ${logoColors.brightYellow}60`,
                                    transform: 'translateY(-2px)',
                                },
                                ...(isActive('/contact') && {
                                    background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                    boxShadow: `0 4px 15px ${logoColors.brightYellow}40`,
                                    border: `1px solid ${logoColors.brightYellow}`,
                                }),
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            Contact Us
                        </Button>

                        {/* <Button
                            onClick={() => navigate('/academy')}
                            variant={isActive('/academy') ? 'contained' : 'text'}
                            sx={{
                                minWidth: 'auto',
                                px: 3,
                                py: 1,
                                color: isActive('/academy') ? logoColors.darkBlue : logoColors.brightYellow,
                                fontWeight: 600,
                                borderRadius: '8px',
                                textTransform: 'none',
                                border: `1px solid ${logoColors.brightYellow}20`,
                                '&:hover': {
                                    backgroundColor: isActive('/academy') 
                                        ? logoColors.lightYellow
                                        : `${logoColors.brightYellow}20`,
                                    border: `1px solid ${logoColors.brightYellow}60`,
                                    transform: 'translateY(-2px)',
                                },
                                ...(isActive('/academy') && {
                                    background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                    boxShadow: `0 4px 15px ${logoColors.brightYellow}40`,
                                    border: `1px solid ${logoColors.brightYellow}`,
                                }),
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            Academy
                        </Button> */}
                        
                        {/* Admin button - uncomment when needed */}
                        {/* <Button
                            onClick={() => navigate('/admin')}
                            variant={isActive('/admin') ? 'contained' : 'text'}
                            sx={{
                                minWidth: 'auto',
                                px: 3,
                                py: 1,
                                color: isActive('/admin') ? logoColors.darkBlue : 'white',
                                fontWeight: 600,
                                borderRadius: '8px',
                                textTransform: 'none',
                                border: `1px solid ${logoColors.brightYellow}20`,
                                '&:hover': {
                                    backgroundColor: isActive('/admin') 
                                        ? logoColors.lightYellow
                                        : `${logoColors.brightYellow}20`,
                                    border: `1px solid ${logoColors.brightYellow}60`,
                                    transform: 'translateY(-2px)',
                                },
                                ...(isActive('/admin') && {
                                    background: `linear-gradient(45deg, ${logoColors.brightYellow} 30%, ${logoColors.lightYellow} 90%)`,
                                    boxShadow: `0 4px 15px ${logoColors.brightYellow}40`,
                                    border: `1px solid ${logoColors.brightYellow}`,
                                }),
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            Admin
                        </Button> */}
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default Header;